import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { CommunicationStatus, CommunicationType } from '@/models'

export interface GetCommunicationDetailsRequest {
  id: number
}

export type CommunicationDetailsClient = {
  id: number
  firstName: string
  lastName: string
}

export type CommunicationDetailsRecipientTypes = {
  // Note: Hand-picked residents (`standaloneClientIds`) who still live in the communication's building
  clients?: CommunicationDetailsClient[]
  // Note: Missing until the BE that returns it is deployed
  userGroupIds?: number[]
}

export interface GetCommunicationDetailsResponse {
  id: number
  title: string
  type: CommunicationType
  message: string
  status: CommunicationStatus
  createdAt: number
  scheduledDate: number | null
  buildingId: number
  recipientsCount: number
  recipientTypes: CommunicationDetailsRecipientTypes
  standaloneClientIds: number[] | null
  mailingList: number | null
  replyTo?: string
  from?: string
  // Note: The BE stores these only when the matching recipient filter was used
  buildingNumber?: string | null
  floor?: (string | number)[] | null
  yardiPropertyId?: string | null
}

export const getCommunicationDetailsQueryKey = (data: GetCommunicationDetailsRequest) => [
  'useGetCommunicationDetails',
  data,
]

export const getCommunicationDetails = async (
  data: GetCommunicationDetailsRequest,
  signal?: AbortSignal,
): Promise<GetCommunicationDetailsResponse> => {
  const response = await authAPI.get<GetCommunicationDetailsResponse>(
    `/communication/${data.id}/details`,
    { signal },
  )

  return response.data
}

export const useGetCommunicationDetails = (data: GetCommunicationDetailsRequest) => {
  return useQuery({
    enabled: !!data.id,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getCommunicationDetails(data, signal),
    queryKey: getCommunicationDetailsQueryKey(data),
  })
}
